import type { RequirementSuggestion } from "@eduviaos/shared";

import type { SelectableNode } from "../lib/collectNodes.js";
import { DiffNodeRow } from "./DiffNodeRow.js";

/**
 * L3 — one proposed stage requirement (Phase F-finale-4 Slice 5).
 *
 * Renders the requirement's label, polymorphic `kind`, and the salient config
 * entries (template slug, pass-mark, manual-check role, document slot) so the
 * reviewer can see exactly what gate they're accepting. Selection is driven
 * by the parent via `checked` + `onToggle`.
 */
export interface AIRequirementCardProps {
  requirement: RequirementSuggestion;
  node: SelectableNode;
  checked: boolean;
  onToggle: () => void;
}

/** Config keys worth surfacing per requirement kind, in display order. */
const CONFIG_DISPLAY_KEYS = ["template_slug", "pass_mark", "role", "slot", "require_grading"];

function configChips(config: Record<string, unknown>): { key: string; value: string }[] {
  return CONFIG_DISPLAY_KEYS.filter((k) => config[k] !== undefined && config[k] !== null).map(
    (k) => ({ key: k, value: String(config[k]) }),
  );
}

export function AIRequirementCard({
  requirement,
  node,
  checked,
  onToggle,
}: AIRequirementCardProps) {
  const { kind, label, config } = requirement.requirement;
  const chips = configChips(config);

  return (
    <DiffNodeRow
      node={node}
      checked={checked}
      onToggle={onToggle}
      ariaLabel={label}
      title={label}
      reason={requirement.reason}
      details={
        <div className="flex flex-wrap items-center gap-2">
          <code className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[11px]">
            {kind}
          </code>
          {chips.map((chip) => (
            <span key={chip.key} className="text-muted-foreground text-[11px]">
              {chip.key.replace(/_/g, " ")}: <span className="text-foreground">{chip.value}</span>
            </span>
          ))}
        </div>
      }
    />
  );
}
